import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  HeartPulse,
  AlertTriangle,
  AlertCircle,
  Info,
  ArrowRight,
  Loader2,
  Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

const SEV_ICON = { critical: AlertCircle, warning: AlertTriangle, info: Info };
const SEV_COLOR = {
  critical: "text-red-400 border-red-500/30 bg-red-500/5",
  warning:  "text-yellow-400 border-yellow-500/30 bg-yellow-500/5",
  info:     "text-gold-light border-gold-subtle bg-gold/5",
};

function interpolate(s, params) {
  if (!params) return s;
  return Object.entries(params).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, String(v)), s);
}

export default function LegacyHealth() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tourActive, setTourActive] = useState(() => !localStorage.getItem("ks_tour_seen"));
  const [resending, setResending] = useState(false);

  useEffect(() => {
    api.get("/legacy/health")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    const tm = setInterval(() => {
      if (localStorage.getItem("ks_tour_seen")) {
        setTourActive(false);
        clearInterval(tm);
      }
    }, 1000);
    return () => clearInterval(tm);
  }, []);

  if (tourActive) return null;

  const handleSignalCta = async (s) => {
    if (s.k === "email_unverified") {
      setResending(true);
      try {
        const r = await api.post("/auth/resend-confirmation");
        if (r.data?.already_verified) {
          toast.success(t("email_already_verified") || "Email already verified");
        } else {
          toast.success(t("email_resent") || "Confirmation email resent");
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error");
      } finally {
        setResending(false);
      }
      return false;
    }
    return true;
  };

  if (loading) {
    return (
      <div className="glass-card rounded-md p-6 mb-8 flex items-center gap-3 text-muted text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("lh_loading")}
      </div>
    );
  }
  if (!data) return null;

  const gradeMap = {
    excellent: { label: t("lh_grade_excellent"), color: "text-emerald-400", bar: "bg-emerald-400" },
    good:      { label: t("lh_grade_good"),      color: "text-gold-light",  bar: "bg-gold" },
    fair:      { label: t("lh_grade_fair"),      color: "text-yellow-400",  bar: "bg-yellow-400" },
    weak:      { label: t("lh_grade_weak"),      color: "text-red-400",     bar: "bg-red-400" },
  };
  const grade = gradeMap[data.grade] || gradeMap.fair;

  const titleFor = (s) => {
    // Pluralisation: if N is exactly 1 and a "_1" variant exists, use it
    const params = s.params || {};
    const N = params.N;
    if (N === 1) {
      const sing = t(`sig_${s.k}_title_1`);
      if (sing && !sing.startsWith("sig_")) return interpolate(sing, params);
    }
    const generic = t(`sig_${s.k}_title`);
    return generic && !generic.startsWith("sig_") ? interpolate(generic, params) : s.title;
  };
  const detailFor = (s) => {
    const x = t(`sig_${s.k}_detail`);
    return x && !x.startsWith("sig_") ? x : s.detail;
  };
  const ctaFor = (s) => {
    const x = t(`sig_${s.k}_cta`);
    return x && !x.startsWith("sig_") ? x : s.cta;
  };

  return (
    <div className="glass-card rounded-md p-6 md:p-8 mb-8" data-testid="legacy-health">
      <div className="flex flex-col md:flex-row md:items-center gap-6 mb-5">
        <div className="flex items-start gap-4 flex-1">
          <HeartPulse className="w-7 h-7 text-gold mt-1 shrink-0" strokeWidth={1.2} />
          <div>
            <div className="font-label text-gold mb-1">{t("lh_label")}</div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-display text-4xl text-gold-light">{data.score}</span>
              <span className="text-faded text-sm">/ 100</span>
              <span className={`text-sm ${grade.color}`}>· {grade.label}</span>
            </div>
          </div>
        </div>
        {data.score >= 85 && (
          <div className="flex items-center gap-2 text-emerald-400 text-sm">
            <Sparkles className="w-4 h-4" /> {t("lh_well_protected")}
          </div>
        )}
      </div>

      <div className="h-1.5 bg-obsidian-2 rounded-full overflow-hidden mb-5">
        <div className={`${grade.bar} h-full transition-all duration-700`} style={{ width: `${data.score}%` }} />
      </div>

      {data.signals.length === 0 ? (
        <div className="text-emerald-300 text-sm flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> {t("lh_all_good")}
        </div>
      ) : (
        <ul className="space-y-2.5" data-testid="legacy-health-signals">
          {data.signals.map((s, i) => {
            const Icon = SEV_ICON[s.severity] || Info;
            const color = SEV_COLOR[s.severity] || SEV_COLOR.info;
            return (
              <li key={`${s.k}-${i}`} className={`flex items-start gap-3 border rounded p-3 ${color}`} data-testid={`signal-${s.k}`}>
                <Icon className="w-4 h-4 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium leading-tight">{titleFor(s)}</div>
                  <div className="text-xs text-muted mt-1 leading-relaxed">{detailFor(s)}</div>
                </div>
                {s.cta_to && s.k !== "email_unverified" && (
                  <Link to={s.cta_to} className="text-xs whitespace-nowrap flex items-center gap-1 hover:opacity-70 transition">
                    {ctaFor(s)} <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
                {s.k === "email_unverified" && (
                  <button
                    onClick={() => handleSignalCta(s)}
                    disabled={resending}
                    className="text-xs whitespace-nowrap flex items-center gap-1 hover:opacity-70 transition disabled:opacity-40"
                    data-testid="signal-email-unverified-resend"
                  >
                    {resending ? t("lh_resending") : ctaFor(s)}
                    {!resending && <ArrowRight className="w-3 h-3" />}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
